import { createMentionTypeaheadProviders } from '#pplx/frontend/composer-sdk-plugins/mention-typeahead/createMentionTypeaheadProviders.js';
import { createPlugin } from '#pplx/frontend/composer-sdk/plugins/createPlugin.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
const EMPTY_PLUGINS = Object.freeze([]);
const EMPTY_RESULT = Object.freeze({ plugins: EMPTY_PLUGINS });
const EMPTY_OPTIONS = Object.freeze([]);
/**
 * Adapts shared Ask mention sections and supplied results to Composer typeahead.
 * @returns Composer plugins; omitting context and options disables typeahead while retaining a supplied icon renderer.
 */
export function useMentionTypeaheadPlugin({ mentionTypeaheadOptions, mentionTypeaheadContext, mentionIconRenderer, includeSlashProviders = true, onTriggerTypeahead, spacesCopyVariant = 'space', }) {
    const { $t } = useIntl();
    const { onTriggerDevices, onTriggerSpaces, onTriggerSkills, shouldUseComputerCommandMenu } = mentionTypeaheadContext ?? {};
    const onTrigger = useCallback((trigger, query) => {
        // A consumer veto must run before shared lazy loaders start work.
        if (onTriggerTypeahead?.(trigger, query) === false)
            return false;
        if (trigger === '@') {
            onTriggerDevices?.();
            onTriggerSpaces?.();
        }
        else if (trigger === '/' && !shouldUseComputerCommandMenu) {
            onTriggerSkills?.();
        }
        return true;
    }, [
        onTriggerTypeahead,
        onTriggerDevices,
        onTriggerSpaces,
        onTriggerSkills,
        shouldUseComputerCommandMenu,
    ]);
    return useMemo(() => {
        if (mentionTypeaheadOptions === undefined && mentionTypeaheadContext === undefined) {
            if (mentionIconRenderer === undefined)
                return EMPTY_RESULT;
            const plugin = createPlugin({
                id: 'mention-typeahead',
                capabilities: [
                    {
                        kind: 'mention',
                        configure: () => ({ renderIcon: mentionIconRenderer }),
                    },
                ],
            });
            return Object.freeze({ plugins: Object.freeze([plugin]) });
        }
        const plugin = createPlugin({
            id: 'mention-typeahead',
            capabilities: [
                {
                    kind: 'typeahead',
                    providers: createMentionTypeaheadProviders({
                        $t,
                        includeSlashProviders,
                        mentionTypeaheadOptions: mentionTypeaheadOptions ?? EMPTY_OPTIONS,
                        mentionTypeaheadContext,
                        onTriggerTypeahead: onTrigger,
                        spacesCopyVariant,
                    }),
                },
                ...(mentionIconRenderer === undefined
                    ? []
                    : [
                        {
                            kind: 'mention',
                            configure: () => ({ renderIcon: mentionIconRenderer }),
                        },
                    ]),
            ],
        });
        return Object.freeze({ plugins: Object.freeze([plugin]) });
    }, [
        $t,
        includeSlashProviders,
        mentionIconRenderer,
        mentionTypeaheadOptions,
        mentionTypeaheadContext,
        onTrigger,
        spacesCopyVariant,
    ]);
}
//# sourceMappingURL=useMentionTypeaheadPlugin.js.map